/**
 * Phone number formatting utilities for PilotCity authentication
 * Handles E.164 formatting and display formatting for phone numbers
 */

/**
 * Format phone number to E.164 format
 * Adds +1 prefix if not present (assumes US/Canada numbers)
 * @param phone Raw phone number input
 * @returns E.164 formatted phone number
 */
export const formatPhoneToE164 = (phone: string): string => {
  if (!phone) return '';

  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  // Check if it already has country code
  if (digitsOnly.startsWith('1') && digitsOnly.length === 11) {
    return `+${digitsOnly}`;
  }

  // Add US country code if 10 digits
  if (digitsOnly.length === 10) {
    return `+1${digitsOnly}`;
  }

  // If already has + prefix, return as is
  if (phone.startsWith('+')) {
    return phone;
  }

  // Default: add +1 prefix
  return `+1${digitsOnly}`;
};

/**
 * Format phone number for display
 * Converts E.164 to readable format: (555) 555-5555
 * @param phone E.164 formatted phone number
 * @returns Display formatted phone number
 */
export const formatPhoneForDisplay = (phone: string): string => {
  if (!phone) return '';

  // Remove country code and non-digits
  const digitsOnly = phone.replace(/^\+?1?/, '').replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  if (digitsOnly.length === 10) {
    const areaCode = digitsOnly.slice(0, 3);
    const firstPart = digitsOnly.slice(3, 6);
    const secondPart = digitsOnly.slice(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  }

  // Return original if can't format
  return phone;
};

/**
 * Validate if a string is a valid phone number
 * @param phone Phone number to validate
 * @returns True if valid phone number
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  if (!phone) return false;

  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');

  // Valid if 10 digits (US) or 11 digits starting with 1 (US with country code)
  return digitsOnly.length === 10 || (digitsOnly.length === 11 && digitsOnly.startsWith('1'));
};

/**
 * Normalize phone number for comparison
 * Strips formatting and ensures consistent format
 * @param phone Phone number to normalize
 * @returns Normalized phone number
 */
export const normalizePhone = (phone: string): string => {
  if (!phone) return '';

  // Get E.164 format then remove the + for comparison
  const e164 = formatPhoneToE164(phone);
  return e164.replace(/^\+/, '');
};
